import { useParams, useNavigate } from 'react-router-dom';
import { vendors } from '@/mocks/vendors';
import { categoryData } from '@/mocks/categories';

export default function VendorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const vendor = vendors.find((v) => v.id === id);

  if (!vendor) {
    return (
      <div className="min-h-[100dvh] bg-cream flex items-center justify-center">
        <p className="text-navy">존재하지 않는 업체입니다</p>
      </div>
    );
  }

  const cat = categoryData[vendor.category as keyof typeof categoryData];

  return (
    <div className="min-h-[100dvh] bg-cream flex flex-col">
      {/* Header Image */}
      <div className="relative h-[280px] shrink-0">
        <img
          src={vendor.image}
          alt={vendor.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        <button
          onClick={() => navigate('/vendors')}
          className="absolute top-5 left-5 w-9 h-9 rounded-full bg-white/20 backdrop-blur flex items-center justify-center text-white cursor-pointer"
        >
          <i className="ri-arrow-left-s-line text-xl" />
        </button>
        <div className="absolute bottom-4 left-5 right-5">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="px-2 py-0.5 rounded text-[10px] font-medium"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#fff' }}
            >
              {cat.name}
            </span>
          </div>
          <h1 className="text-white text-xl font-bold">{vendor.name}</h1>
          <p className="text-white/80 text-sm mt-0.5">{vendor.region} · {vendor.priceRange}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Matching Rate */}
        <div className="px-5 py-5">
          <div className="bg-white rounded-2xl p-4 border border-cream-dark flex items-center justify-between">
            <div>
              <p className="text-beige-dark text-xs">나의 조건과의 매칭률</p>
              <p className="text-navy font-bold text-lg mt-0.5">{vendor.matchRate}%</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-softgold/15 flex items-center justify-center">
              <i className="ri-heart-3-fill text-softgold text-lg" />
            </div>
          </div>
        </div>

        {/* Recommend Reason */}
        <div className="px-5 pb-5">
          <div className="bg-navy rounded-2xl p-5 text-white">
            <div className="flex items-center gap-2 mb-3">
              <i className="ri-lightbulb-flash-line text-softgold" />
              <p className="font-semibold text-sm">다이렉트 추천 요약</p>
            </div>
            <p className="text-cream-dark text-sm leading-relaxed">{vendor.recommendReason}</p>
          </div>
        </div>

        {/* Matching Tags */}
        <div className="px-5 pb-5">
          <p className="text-navy font-semibold text-sm mb-3">내 조건과 맞는 이유</p>
          <div className="flex flex-wrap gap-2">
            {vendor.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1.5 rounded-full bg-white text-navy text-xs font-medium border border-cream-dark"
              >
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Review Summary */}
        <div className="px-5 pb-5">
          <p className="text-navy font-semibold text-sm mb-3">실제 리뷰 요약</p>
          <div className="bg-white rounded-2xl p-4 border border-cream-dark space-y-3">
            <div className="flex items-start gap-2">
              <i className="ri-chat-smile-3-line text-softgold mt-0.5 shrink-0" />
              <p className="text-gray-600 text-sm leading-relaxed">
                "상담이 정말 친절하고 전문적이었어요. 예복 제작 과정도 상세히 설명해주셔서 믿음이 갔어요."
              </p>
            </div>
            <div className="flex items-start gap-2">
              <i className="ri-chat-smile-3-line text-softgold mt-0.5 shrink-0" />
              <p className="text-gray-600 text-sm leading-relaxed">
                "가격대비 퀄리티가 훌륭해요. 본식 날 하객들이 다 예복 어디서 맞췄냐고 물어봤어요."
              </p>
            </div>
          </div>
        </div>

        {/* Pre-consultation Checklist */}
        <div className="px-5 pb-5">
          <p className="text-navy font-semibold text-sm mb-3">상담 전 체크포인트</p>
          <div className="bg-white rounded-2xl p-4 border border-cream-dark space-y-3">
            {[
              '예산 범위 먼저 확인하기',
              '촬영용/본식용 구분 여부 확인',
              '수선 비용과 기간 확인',
              '구성품 포함 여부 체크',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-5 h-5 rounded bg-cream flex items-center justify-center shrink-0">
                  <i className="ri-check-line text-navy text-xs" />
                </div>
                <span className="text-gray-600 text-sm">{item}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Photo Gallery */}
        <div className="px-5 pb-6">
          <p className="text-navy font-semibold text-sm mb-3">업체 제공 사진</p>
          <div className="grid grid-cols-2 gap-2">
            {vendors.slice(0, 4).map((v) => (
              <div key={v.id} className="rounded-xl overflow-hidden h-[120px]">
                <img
                  src={v.image}
                  alt={v.name}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Actions */}
      <div className="px-5 pb-[env(safe-area-inset-bottom)] pt-3 bg-white border-t border-cream-dark shrink-0">
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/quote')}
            className="flex-1 bg-navy text-white rounded-xl py-4 font-semibold text-base cursor-pointer hover:bg-navy-light transition-colors whitespace-nowrap"
          >
            견적 요청
          </button>
          <button
            onClick={() => {}}
            className="flex-1 bg-cream text-navy rounded-xl py-4 font-semibold text-base border border-cream-dark cursor-pointer hover:bg-cream-dark transition-colors whitespace-nowrap"
          >
            상담 예약
          </button>
        </div>
      </div>
    </div>
  );
}